import React, { useState, useEffect, useRef } from 'react';

/**
 * AdminMockData — Mock trading control panel.
 *
 * Section 1: Historical Seeder  — one-time 60-day bulk insert
 * Section 2: Live Simulator     — ongoing bets placed every 30s, 90-day ramp
 */

const StatCard = ({ label, value, sub, color = 'white' }) => (
    <div className="bg-black/30 border border-white/5 rounded p-4 text-center">
        <div className="text-[9px] uppercase tracking-[0.3em] text-gray-500 mb-1">{label}</div>
        <div className={`text-xl font-mono font-bold text-${color}-400`}>{value}</div>
        {sub && <div className="text-[9px] text-gray-600 mt-0.5">{sub}</div>}
    </div>
);

const AdminMockData = () => {
    const [status,    setStatus]    = useState(null);
    const [simStatus, setSimStatus] = useState(null);
    const [progress,  setProgress]  = useState([]);
    const [generating, setGenerating] = useState(false);
    const [preview,   setPreview]   = useState(null);
    const [error,     setError]     = useState('');
    const [confirm,   setConfirm]   = useState('');
    const [simDay,    setSimDay]    = useState(60);
    const pollRef = useRef(null);
    const simPollRef = useRef(null);
    const logRef  = useRef(null);

    const apiFetch = (path, opts = {}) =>
        fetch(`/api/admin/mock${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts });

    const loadStatus = async () => {
        try {
            const r = await apiFetch('/status');
            const d = await r.json();
            setStatus(d);
            setSimStatus(d.sim);
            setGenerating(d.generating);
        } catch { /* ignore */ }
    };

    const loadSimStatus = async () => {
        try {
            const r = await apiFetch('/sim/status');
            const d = await r.json();
            setSimStatus(d);
        } catch { /* ignore */ }
    };

    const pollProgress = async () => {
        try {
            const r = await apiFetch('/progress');
            const d = await r.json();
            setProgress(d.lines || []);
            setGenerating(d.running);
            if (!d.running) {
                clearInterval(pollRef.current);
                loadStatus();
            }
            if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
        } catch { /* ignore */ }
    };

    useEffect(() => {
        loadStatus();
        // Poll sim status every 5s regardless
        simPollRef.current = setInterval(loadSimStatus, 5000);
        return () => {
            clearInterval(pollRef.current);
            clearInterval(simPollRef.current);
        };
    }, []);

    /* ── Seeder handlers ── */
    const handleDryRun = async () => {
        setError(''); setPreview(null);
        const r = await apiFetch('/generate', { method: 'POST', body: JSON.stringify({ dryRun: true }) });
        const d = await r.json();
        if (d.ok) setPreview(d); else setError(d.error || 'Dry run failed');
    };

    const handleGenerate = async () => {
        setConfirm(''); setError(''); setProgress([]);
        const r = await apiFetch('/generate', { method: 'POST', body: JSON.stringify({ dryRun: false }) });
        const d = await r.json();
        if (!d.ok && !d.started) { setError(d.error || 'Failed'); return; }
        setGenerating(true);
        pollRef.current = setInterval(pollProgress, 1500);
    };

    const handleClear = async () => {
        setConfirm(''); setError('');
        const r = await apiFetch('/clear', { method: 'POST' });
        const d = await r.json();
        if (!d.ok) setError(d.error || 'Clear failed');
        else { setPreview(null); setProgress([]); loadStatus(); }
    };

    /* ── Simulator handlers ── */
    const handleSimStart = async () => {
        setConfirm(''); setError('');
        const r = await apiFetch('/sim/start', { method: 'POST', body: JSON.stringify({ simDay }) });
        const d = await r.json();
        if (!d.ok) setError(d.message || d.error || 'Failed to start');
        await loadSimStatus();
    };

    const handleSimStop = async () => {
        await apiFetch('/sim/stop', { method: 'POST' });
        await loadSimStatus();
    };

    const hasHistory  = status?.active;
    const simRunning  = simStatus?.running;

    return (
        <div className="p-6 max-w-5xl mx-auto text-gray-300 space-y-8">

            {/* ══ LIVE SIMULATOR ══ */}
            <section>
                <div className="flex items-baseline justify-between mb-4 border-b border-gray-800 pb-3">
                    <div>
                        <h2 className="text-xl font-light tracking-[0.2em] uppercase text-white">
                            🤖 Live Simulator
                        </h2>
                        <p className="text-gray-500 text-xs mt-1">
                            Places real bets every 30 seconds using 5,000 mock accounts. Follows the
                            90-day DAU ramp (Day 60 → 1,982 active, Day 90 → 3,790 active).
                            The existing settlement engine handles all payouts automatically.
                        </p>
                    </div>
                    <div className={`text-[10px] font-black px-3 py-1 rounded uppercase tracking-widest border ${
                        simRunning
                            ? 'text-green-400 border-green-500/30 bg-green-500/10 animate-pulse'
                            : 'text-gray-500 border-gray-700 bg-black/20'
                    }`}>
                        {simRunning ? '● LIVE' : '○ IDLE'}
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                    <StatCard label="Sim Day" value={simStatus?.simDay ?? '—'} color="blue" />
                    <StatCard label="Target DAU" value={simStatus?.targetDAU?.toLocaleString() ?? '—'} color="purple" />
                    <StatCard label="Bets/Min" value={simStatus?.betsPerMinute ?? '—'} color="yellow" />
                    <StatCard label="Total Bets Placed"
                              value={simStatus?.stats?.betsPlaced?.toLocaleString() ?? '0'} color="green"
                              sub={`${simStatus?.stats?.ticks ?? 0} ticks`} />
                </div>

                {/* DAU Ramp Reference */}
                <div className="bg-black/20 border border-white/5 rounded p-3 mb-4 text-[10px] font-mono text-gray-500">
                    <span className="text-gray-400 font-black mr-3">90-DAY RAMP:</span>
                    Day 1→30: <span className="text-blue-400">50–812 DAU</span>
                    <span className="mx-2">·</span>
                    Day 31→60: <span className="text-purple-400">812–1,982 DAU</span>
                    <span className="mx-2">·</span>
                    Day 61→90: <span className="text-green-400">1,982–3,790 DAU</span>
                </div>

                {error && (
                    <div className="mb-3 p-3 bg-red-900/20 border border-red-500/30 rounded text-red-400 text-xs font-mono">
                        ❌ {error}
                    </div>
                )}

                {!simRunning ? (
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <label className="text-[10px] uppercase tracking-widest text-gray-500 font-black">Start at Day</label>
                            <input
                                type="number" min={1} max={90} value={simDay}
                                onChange={e => setSimDay(parseInt(e.target.value) || 60)}
                                className="w-16 bg-black/40 border border-white/10 rounded px-2 py-1 text-xs font-mono text-white text-center outline-none focus:border-blue-500/50"
                            />
                            <span className="text-[9px] text-gray-600">
                                (~{Math.round(simDay <= 30 ? 50 + (simDay-1)/29*(812-50) : simDay <= 60 ? 812+(simDay-30)/30*(1982-812) : 1982+(simDay-60)/30*(3790-1982))} DAU)
                            </span>
                        </div>
                        <button
                            id="sim-start-btn"
                            onClick={() => setConfirm('sim-start')}
                            className="px-4 py-2 bg-green-500/10 border border-green-500/30 rounded text-[10px] uppercase tracking-widest font-black text-green-400 hover:bg-green-500/20 transition-all"
                        >
                            ▶ Start Live Simulation
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-4">
                        <div className="text-xs text-gray-400 font-mono">
                            Running since {simStatus?.startedAt ? new Date(simStatus.startedAt).toLocaleTimeString() : '—'}
                            <span className="mx-2">·</span>
                            {simStatus?.mockUsers?.toLocaleString()} mock users loaded
                        </div>
                        <button
                            id="sim-stop-btn"
                            onClick={handleSimStop}
                            className="px-4 py-2 bg-red-500/10 border border-red-500/30 rounded text-[10px] uppercase tracking-widest font-black text-red-400 hover:bg-red-500/20 transition-all"
                        >
                            ■ Stop Simulation
                        </button>
                    </div>
                )}

                {confirm === 'sim-start' && (
                    <div className="mt-3 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded text-xs text-yellow-300">
                        <p className="mb-3">
                            ⚠️ This will create <strong>5,000 mock user accounts</strong> (if not already present)
                            and begin placing <strong>~{Math.round(Math.min(75,(simDay<=30?50+(simDay-1)/29*(812-50):simDay<=60?812+(simDay-30)/30*(1982-812):1982+(simDay-60)/30*(3790-1982))*50/1440))*2} bets/minute</strong> into live pools.
                            Bets will be settled by the normal settlement engine.
                            Real users will see mock activity in the pool totals.
                        </p>
                        <div className="flex gap-3">
                            <button onClick={handleSimStart} className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded text-[10px] font-black uppercase tracking-widest">Start</button>
                            <button onClick={() => setConfirm('')} className="px-3 py-1.5 border border-white/10 text-gray-400 rounded text-[10px] font-black uppercase tracking-widest hover:text-white">Cancel</button>
                        </div>
                    </div>
                )}
            </section>

            {/* ══ HISTORICAL SEEDER ══ */}
            <section>
                <div className="mb-4 border-b border-gray-800 pb-3">
                    <h2 className="text-xl font-light tracking-[0.2em] uppercase text-white">
                        📂 Historical Seeder
                    </h2>
                    <p className="text-gray-500 text-xs mt-1">
                        One-time bulk insert of 60 days of past bets, pools and settlements (~90K rows).
                        Populates Trade Log, Archive, and Admin Overview history.
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
                    <StatCard label="Mock Users" value={status?.mockUsers ?? '—'} color="blue" />
                    <StatCard label="Mock Bets"  value={status?.mockBets?.toLocaleString() ?? '—'} color="purple" />
                    <StatCard label="House Credits (mock)" value={status?.houseCredits?.toLocaleString() ?? '—'} color="green" />
                </div>

                <div className="flex flex-wrap gap-3 mb-4">
                    {!hasHistory && !generating && (
                        <>
                            <button id="mock-dry-run-btn" onClick={handleDryRun}
                                className="px-4 py-2 border border-white/10 rounded text-[10px] uppercase tracking-widest font-black text-gray-400 hover:text-white hover:border-white/20 transition-all">
                                🔍 Preview (Dry Run)
                            </button>
                            <button id="mock-generate-btn" onClick={() => setConfirm('generate')}
                                className="px-4 py-2 border border-blue-500/30 bg-blue-500/10 rounded text-[10px] uppercase tracking-widest font-black text-blue-400 hover:bg-blue-500/20 transition-all">
                                ⬇ Seed 60-Day History
                            </button>
                        </>
                    )}
                    {hasHistory && !generating && (
                        <button id="mock-clear-btn" onClick={() => setConfirm('clear')}
                            className="px-4 py-2 border border-red-500/30 bg-red-500/10 rounded text-[10px] uppercase tracking-widest font-black text-red-400 hover:bg-red-500/20 transition-all">
                            🗑 Clear Historical Data
                        </button>
                    )}
                    {generating && <div className="text-yellow-400 text-xs font-mono animate-pulse">⏳ Generating history…</div>}
                </div>

                {confirm === 'generate' && (
                    <div className="mb-4 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded text-xs text-yellow-300">
                        <p className="mb-3">Insert ~90,000 historical bets across 1,000 mock accounts. Takes 3-8 mins. Continue?</p>
                        <div className="flex gap-3">
                            <button onClick={handleGenerate} className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded text-[10px] font-black uppercase tracking-widest">Yes, Generate</button>
                            <button onClick={() => setConfirm('')} className="px-3 py-1.5 border border-white/10 text-gray-400 rounded text-[10px] font-black uppercase tracking-widest hover:text-white">Cancel</button>
                        </div>
                    </div>
                )}
                {confirm === 'clear' && (
                    <div className="mb-4 p-4 bg-red-900/20 border border-red-500/30 rounded text-xs text-red-300">
                        <p className="mb-3">Delete all mock historical data. Real user data is unaffected.</p>
                        <div className="flex gap-3">
                            <button onClick={handleClear} className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded text-[10px] font-black uppercase tracking-widest">Yes, Clear</button>
                            <button onClick={() => setConfirm('')} className="px-3 py-1.5 border border-white/10 text-gray-400 rounded text-[10px] font-black uppercase tracking-widest hover:text-white">Cancel</button>
                        </div>
                    </div>
                )}
                {preview && (
                    <div className="mb-4 p-4 bg-blue-900/20 border border-blue-500/20 rounded text-xs font-mono text-blue-300 space-y-1">
                        <div className="font-black text-blue-400 mb-2">🔍 Dry Run Preview</div>
                        <div>Users: <strong>{preview.users?.toLocaleString()}</strong> · Slots: <strong>{preview.slots?.toLocaleString()}</strong> · Pools: <strong>{(preview.slots * 3)?.toLocaleString()}</strong></div>
                        <div className="text-gray-500 mt-1">No data written.</div>
                    </div>
                )}
                {(generating || progress.length > 0) && (
                    <div className="bg-black/60 border border-white/5 rounded overflow-hidden">
                        <div className="px-3 py-2 border-b border-white/5 text-[9px] uppercase tracking-widest text-gray-500 font-black flex justify-between">
                            <span>Seeder Log</span>
                            {generating && <span className="text-yellow-400 animate-pulse">● RUNNING</span>}
                        </div>
                        <div ref={logRef} className="p-3 font-mono text-[10px] text-green-400 overflow-y-auto max-h-48 space-y-0.5">
                            {progress.map((line, i) => <div key={i}>{line}</div>)}
                        </div>
                    </div>
                )}
            </section>

            <div className="text-[9px] text-gray-700 border-t border-white/5 pt-4">
                Mock users: <code className="text-gray-500">@mockuser.minutetest</code> domain · Prefixed <code className="text-gray-500">##MOCK##</code> · Cannot withdraw real SOL · Stop simulation and clear data before public launch.
            </div>
        </div>
    );
};

export default AdminMockData;
